import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Lock, Star, Trash2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { TripExpenses } from "../components/TripExpenses";
import { RuteCombobox } from "../components/RuteCombobox";
import { ScanSlipButton } from "../components/ScanSlipButton";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../api";
import { queueRequest, isNetworkError } from "../offline";

const KOMODITAS = ["CPO", "Palm Kernel", "Cangkang"];
const STATUS = ["Draft", "Berangkat", "Bongkar", "Selesai"];

const emptyForm = {
  Tanggal_Muat: new Date().toISOString().slice(0, 10),
  No_DO: "",
  Plat_Truk: "",
  Nama_Supir: "",
  Jenis_Komoditas: "",
  PKS_Asal: "",
  Tujuan_Bongkar: "",
  Odometer_Awal_KM: "",
  Odometer_Akhir_KM: "",
  Tonase_Muat_KG: "",
  Tonase_Bongkar_KG: "",
  Status_Trip: "Draft",
};

export default function TripFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEdit = !!id;
  const [form, setForm] = useState(emptyForm);
  const [idTrip, setIdTrip] = useState(null);
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [ruteOptions, setRuteOptions] = useState({ pks_asal: [], tujuan_bongkar: [] });
  const [ruteFavorit, setRuteFavorit] = useState([]);

  const loadFavorit = () => {
    api.get("/rute").then(({ data }) => {
      setRuteFavorit(data);
      localStorage.setItem("cache_rute_fav", JSON.stringify(data));
    }).catch(() => setRuteFavorit(JSON.parse(localStorage.getItem("cache_rute_fav") || "[]")));
  };
  useEffect(loadFavorit, []);

  const favoritAktif = ruteFavorit.find((r) =>
    r.PKS_Asal.toLowerCase() === form.PKS_Asal.trim().toLowerCase() &&
    r.Tujuan_Bongkar.toLowerCase() === form.Tujuan_Bongkar.trim().toLowerCase());
  const favoritAda = !!favoritAktif;

  const jarakAktual = form.Odometer_Awal_KM !== "" && form.Odometer_Akhir_KM !== ""
    ? parseInt(form.Odometer_Akhir_KM) - parseInt(form.Odometer_Awal_KM) : null;
  const jarakRute = favoritAktif?.Jarak_KM || null;
  const toleransi = jarakRute ? Math.max(jarakRute * 0.1, 10) : 0;
  const jarakMenyimpang = jarakRute && jarakAktual !== null && Math.abs(jarakAktual - jarakRute) > toleransi;

  const simpanFavorit = async () => {
    try {
      await api.post("/rute", {
        PKS_Asal: form.PKS_Asal, Tujuan_Bongkar: form.Tujuan_Bongkar,
        Jarak_KM: jarakAktual && jarakAktual > 0 ? jarakAktual : null,
      });
      toast.success(jarakAktual && jarakAktual > 0
        ? `Rute favorit tersimpan (jarak ${jarakAktual} KM)`
        : "Rute favorit tersimpan");
      loadFavorit();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const simpanJarakRute = async () => {
    if (!favoritAktif || !jarakAktual || jarakAktual <= 0) return;
    try {
      await api.put(`/rute/${favoritAktif.id}`, { Jarak_KM: jarakAktual });
      toast.success(`Jarak rute diperbarui menjadi ${jarakAktual} KM`);
      loadFavorit();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const hapusFavorit = async (r) => {
    try {
      await api.delete(`/rute/${r.id}`);
      toast.success("Rute favorit dihapus");
      loadFavorit();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    api.get("/trips/rute-options").then(({ data }) => {
      setRuteOptions(data);
      localStorage.setItem("cache_rute", JSON.stringify(data));
    }).catch(() => setRuteOptions(JSON.parse(localStorage.getItem("cache_rute") || '{"pks_asal":[],"tujuan_bongkar":[]}')));
  }, []);

  useEffect(() => {
    api.get("/kendaraan").then(({ data }) => {
      setTrucks(data);
      localStorage.setItem("cache_kendaraan", JSON.stringify(data));
    }).catch(() => setTrucks(JSON.parse(localStorage.getItem("cache_kendaraan") || "[]")));
    api.get("/supir").then(({ data }) => {
      setDrivers(data);
      localStorage.setItem("cache_supir", JSON.stringify(data));
    }).catch(() => setDrivers(JSON.parse(localStorage.getItem("cache_supir") || "[]")));
    if (isEdit) {
      api.get(`/trips/${id}`)
        .then(({ data }) => {
          setIdTrip(data.ID_Trip);
          setForm({
            ...emptyForm,
            ...Object.fromEntries(
              Object.keys(emptyForm).map((k) => [k, data[k] == null ? "" : String(data[k])])
            ),
          });
          setLoading(false);
        })
        .catch(() => {
          toast.error("Trip tidak ditemukan");
          navigate("/trips");
        });
    }
  }, [id, isEdit, navigate]);

  const set = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));
  const setInput = (key) => (e) => set(key)(e.target.value);

  const unlocked = ["Bongkar", "Selesai"].includes(form.Status_Trip);

  const applyScan = (data) => {
    let platWarn = null;
    setForm((f) => {
      const next = { ...f };
      if (data.komoditas) next.Jenis_Komoditas = data.komoditas;
      if (data.plat_normalized) {
        const match = trucks.find((t) => (t.Plat_Nomor || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase() === data.plat_normalized);
        if (match) next.Plat_Truk = match.Plat_Nomor;
        else platWarn = `Plat ${data.vec_no} dari slip tidak ditemukan di Master Kendaraan`;
      }
      if (data.net_weight_kg != null) {
        if (["Bongkar", "Selesai"].includes(f.Status_Trip) && f.Tonase_Muat_KG !== "") next.Tonase_Bongkar_KG = String(data.net_weight_kg);
        else next.Tonase_Muat_KG = String(data.net_weight_kg);
      }
      return next;
    });
    if (platWarn) toast.warning(platWarn, { duration: 8000 });
    if (data.foto_saved) toast.info("Foto slip diarsipkan ke Foto Bukti Timbang trip", { duration: 5000 });
    if (data.net_weight_kg != null) {
      toast.success(`Slip terbaca: ${data.vec_no || "-"} • ${data.product_code || "-"} • ${Number(data.net_weight_kg).toLocaleString("id-ID")} KG`, { duration: 6000 });
    } else {
      toast.error("Berat (NET WEIGHT) tidak terbaca dari slip");
    }
  };
  const handleStatusChange = (val) => {
    setForm((f) => {
      const next = { ...f, Status_Trip: val };
      if (!["Bongkar", "Selesai"].includes(val)) {
        next.Odometer_Akhir_KM = "";
        next.Tonase_Bongkar_KG = "";
      }
      return next;
    });
  };

  const penyusutanRaw =
    form.Tonase_Muat_KG !== "" && form.Tonase_Bongkar_KG !== ""
      ? parseFloat(form.Tonase_Bongkar_KG) - parseFloat(form.Tonase_Muat_KG)
      : null;
  const penyusutan =
    form.Tonase_Muat_KG !== "" && form.Tonase_Bongkar_KG !== ""
      ? (parseFloat(form.Tonase_Bongkar_KG) - parseFloat(form.Tonase_Muat_KG)).toLocaleString("id-ID", { maximumFractionDigits: 2 })
      : "-";

  const jarak =
    form.Odometer_Awal_KM !== "" && form.Odometer_Akhir_KM !== ""
      ? (parseInt(form.Odometer_Akhir_KM) - parseInt(form.Odometer_Awal_KM)).toLocaleString("id-ID")
      : "-";

  const notifyPaketOtomatis = (data) => {
    const p = data?.paket_otomatis;
    if (p?.ditambah) {
      toast.info(`Paket biaya ${p.komoditas}${p.jenis_truk ? ` (${p.jenis_truk})` : ""} otomatis diterapkan: ${p.ditambah} kategori, Rp ${Number(p.total_rp).toLocaleString("id-ID")} — status Sudah Dicairkan`, { duration: 7000 });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.Tanggal_Muat || !form.No_DO.trim() || !form.PKS_Asal.trim() || !form.Tujuan_Bongkar.trim()) {
      toast.error("Lengkapi Tanggal Muat, No. DO, PKS Asal, dan Tujuan Bongkar");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      Odometer_Awal_KM: form.Odometer_Awal_KM === "" ? null : parseInt(form.Odometer_Awal_KM),
      Odometer_Akhir_KM: form.Odometer_Akhir_KM === "" ? null : parseInt(form.Odometer_Akhir_KM),
      Tonase_Muat_KG: form.Tonase_Muat_KG === "" ? null : parseFloat(form.Tonase_Muat_KG),
      Tonase_Bongkar_KG: form.Tonase_Bongkar_KG === "" ? null : parseFloat(form.Tonase_Bongkar_KG),
    };
    try {
      if (isEdit) {
        const { data } = await api.put(`/trips/${id}`, payload);
        toast.success(`Trip ${idTrip} berhasil diperbarui`);
        notifyPaketOtomatis(data);
      } else {
        const { data } = await api.post("/trips", payload);
        toast.success(`Trip ${data.ID_Trip} berhasil dibuat`);
        notifyPaketOtomatis(data);
      }
      navigate("/trips");
    } catch (err) {
      if (isNetworkError(err)) {
        await queueRequest({
          method: isEdit ? "put" : "post",
          url: isEdit ? `/trips/${id}` : "/trips",
          payload,
          label: `Trip DO ${form.No_DO || "-"} (${form.Plat_Truk || "?"})`,
        });
        toast.warning('Koneksi terputus — data disimpan di perangkat sebagai "Tertunda" dan akan terkirim otomatis saat online', { duration: 6000 });
        navigate("/trips");
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-20 text-zinc-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );

  return (
    <div data-testid="trip-form-page" className="max-w-5xl space-y-6">
      <div className="flex items-center gap-3">
        <button data-testid="back-button" onClick={() => navigate("/trips")}
          className="p-2 rounded-md text-zinc-500 hover:bg-zinc-200 transition-colors duration-200">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-900">
            {isEdit ? `Edit Trip ${idTrip || ""}` : "Tambah Trip Baru"}
          </h1>
          <p className="text-sm text-zinc-500 mt-0.5">
            {isEdit ? "Perbarui data perjalanan truk" : "ID Trip akan dibuat otomatis (TRIP-YYYY-XXXX)"}
          </p>
        </div>
      </div>

      <div className="space-y-6">
        <Card className="border-zinc-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-base font-semibold tracking-tight">Informasi Utama</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="space-y-1.5">
              <Label>Tanggal Muat *</Label>
              <Input data-testid="input-tanggal-muat" type="date" required value={form.Tanggal_Muat} onChange={setInput("Tanggal_Muat")} className="bg-white input-touch" />
            </div>
            <div className="space-y-1.5">
              <Label>No. DO *</Label>
              <Input data-testid="input-no-do" required maxLength={50} placeholder="cth: DO-00123" value={form.No_DO} onChange={setInput("No_DO")} className="bg-white input-touch" />
            </div>
            <div className="space-y-1.5">
              <Label>Status Trip *</Label>
              <Select value={form.Status_Trip} onValueChange={handleStatusChange}>
                <SelectTrigger data-testid="select-status-trip" className="bg-white btn-touch"><SelectValue placeholder="Pilih status" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {STATUS.map((s) => <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-base font-semibold tracking-tight">Armada &amp; Komoditas</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="space-y-1.5">
              <Label>Plat Truk *</Label>
              <Select value={form.Plat_Truk} onValueChange={set("Plat_Truk")} required>
                <SelectTrigger data-testid="select-plat-truk" className="bg-white"><SelectValue placeholder="Pilih plat truk" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {trucks.map((t) => (
                    <SelectItem key={t.id} value={t.Plat_Nomor} disabled={t.Status_Unit !== "Aktif"}>
                      {t.Plat_Nomor}{t.Jenis_Truk ? ` — ${t.Jenis_Truk}` : ""}{t.Status_Unit !== "Aktif" ? ` (${t.Status_Unit})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Nama Supir *</Label>
              <Select value={form.Nama_Supir} onValueChange={set("Nama_Supir")} required>
                <SelectTrigger data-testid="select-nama-supir" className="bg-white btn-touch"><SelectValue placeholder="Pilih supir" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {drivers.map((d) => (
                    <SelectItem key={d.id} value={d.Nama_Supir} disabled={d.Status_Supir !== "Aktif"}>
                      {d.Nama_Supir}{d.Status_Supir !== "Aktif" ? ` (${d.Status_Supir})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Jenis Komoditas *</Label>
              <Select value={form.Jenis_Komoditas} onValueChange={set("Jenis_Komoditas")} required>
                <SelectTrigger data-testid="select-jenis-komoditas" className="bg-white btn-touch"><SelectValue placeholder="Pilih komoditas" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {KOMODITAS.map((k) => <SelectItem key={k} value={k} data-testid={`komoditas-option-${k}`}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base font-semibold tracking-tight">Rute Perjalanan</CardTitle>
              <div className="flex items-center gap-2">
                {ruteFavorit.length > 0 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button data-testid="rute-favorit-button" type="button" size="sm" variant="outline"
                        className="h-8 border-amber-300 text-amber-800 hover:bg-amber-50">
                        <Star className="w-3.5 h-3.5 mr-1.5 fill-amber-400 text-amber-400" /> Rute Favorit
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-1 bg-white" align="end">
                      {ruteFavorit.map((r) => (
                        <div key={r.id} className="flex items-center gap-1 rounded-sm hover:bg-zinc-50">
                          <button type="button" data-testid={`rute-favorit-item-${r.id}`}
                            className="flex-1 text-left px-2 py-1.5 text-sm"
                            onClick={() => setForm((f) => ({ ...f, PKS_Asal: r.PKS_Asal, Tujuan_Bongkar: r.Tujuan_Bongkar }))}>
                            {r.PKS_Asal} <span className="text-zinc-400">→</span> {r.Tujuan_Bongkar}
                            {r.Jarak_KM ? <span className="text-xs text-zinc-400"> (±{Number(r.Jarak_KM).toLocaleString("id-ID")} KM)</span> : null}
                          </button>
                          <button type="button" data-testid={`rute-favorit-hapus-${r.id}`}
                            className="p-1.5 text-zinc-300 hover:text-red-600" onClick={() => hapusFavorit(r)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </PopoverContent>
                  </Popover>
                )}
                {form.PKS_Asal.trim() && form.Tujuan_Bongkar.trim() && !favoritAda && (
                  <Button data-testid="rute-simpan-favorit" type="button" size="sm" variant="outline"
                    className="h-8 border-zinc-300 text-zinc-600 hover:bg-amber-50 hover:text-amber-800"
                    onClick={simpanFavorit} title="Simpan pasangan rute ini sebagai favorit">
                    <Star className="w-3.5 h-3.5 mr-1.5" /> Simpan Favorit
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-1.5">
              <Label>PKS Asal *</Label>
              <RuteCombobox testId="input-pks-asal" placeholder="cth: PKS Sei Mangkei" options={ruteOptions.pks_asal}
                value={form.PKS_Asal} onChange={(v) => setForm((f) => ({ ...f, PKS_Asal: v }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Tujuan Bongkar *</Label>
              <RuteCombobox testId="input-tujuan-bongkar" placeholder="cth: Refinery Belawan" options={ruteOptions.tujuan_bongkar}
                value={form.Tujuan_Bongkar} onChange={(v) => setForm((f) => ({ ...f, Tujuan_Bongkar: v }))} />
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-base font-semibold tracking-tight">Odometer &amp; Tonase</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="space-y-1.5">
                <Label>Odometer Awal (KM)</Label>
                <Input data-testid="input-odometer-awal" type="number" inputMode="numeric" min="0" step="1" placeholder="0" value={form.Odometer_Awal_KM} onChange={setInput("Odometer_Awal_KM")} className="bg-white input-touch num-hc text-zinc-900" />
              </div>
              <div className="space-y-1.5">
                <Label className={!unlocked ? "text-zinc-400" : ""}>Odometer Akhir (KM) {!unlocked && <Lock className="w-3 h-3 inline ml-1" />}</Label>
                <Input data-testid="input-odometer-akhir" type="number" inputMode="numeric" min="0" step="1" disabled={!unlocked}
                  placeholder={unlocked ? "0" : "Terkunci"} value={form.Odometer_Akhir_KM} onChange={setInput("Odometer_Akhir_KM")}
                  className="bg-white input-touch num-hc text-zinc-900 disabled:bg-zinc-100 disabled:cursor-not-allowed" />
                {!unlocked && <p className="text-[11px] text-zinc-400">Terbuka saat status "Bongkar" / "Selesai"</p>}
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-500">Jarak Tempuh (KM)</Label>
                <div data-testid="display-jarak-tempuh" className={`min-h-10 flex items-center px-3 rounded-md border bg-white num-hc ${jarakMenyimpang ? "border-amber-500 text-amber-800" : "border-zinc-400 text-zinc-900"}`}>{jarak}</div>
                {jarakRute && jarakAktual !== null && (
                  jarakMenyimpang ? (
                    <p data-testid="jarak-warning" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                      ⚠ Jarak tempuh {jarakAktual.toLocaleString("id-ID")} KM menyimpang dari jarak rute favorit (±{Number(jarakRute).toLocaleString("id-ID")} KM). Periksa kembali odometer akhir.{" "}
                      <button type="button" data-testid="jarak-update-rute" onClick={simpanJarakRute} className="underline font-medium">Perbarui jarak rute</button>
                    </p>
                  ) : (
                    <p data-testid="jarak-ok" className="text-xs text-emerald-700">✓ Sesuai jarak rute favorit (±{Number(jarakRute).toLocaleString("id-ID")} KM)</p>
                  )
                )}
                {favoritAktif && !jarakRute && jarakAktual > 0 && (
                  <button type="button" data-testid="jarak-set-rute" onClick={simpanJarakRute}
                    className="text-xs text-emerald-700 underline">Simpan {jarakAktual.toLocaleString("id-ID")} KM sebagai jarak rute ini</button>
                )}
              </div>
            </div>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-xs text-zinc-500">Isi otomatis plat, komoditas &amp; tonase dari foto slip timbang</p>
              <ScanSlipButton testId="scan-slip-form" tripId={id} onResult={applyScan} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="space-y-1.5">
                <Label>Tonase Muat (KG)</Label>
                <Input data-testid="input-tonase-muat" type="number" inputMode="decimal" min="0" step="0.01" placeholder="0.00" value={form.Tonase_Muat_KG} onChange={setInput("Tonase_Muat_KG")} className="bg-white input-touch num-hc text-zinc-900" />
              </div>
              <div className="space-y-1.5">
                <Label className={!unlocked ? "text-zinc-400" : ""}>Tonase Bongkar (KG) {!unlocked && <Lock className="w-3 h-3 inline ml-1" />}</Label>
                <Input data-testid="input-tonase-bongkar" type="number" inputMode="decimal" min="0" step="0.01" disabled={!unlocked}
                  placeholder={unlocked ? "0.00" : "Terkunci"} value={form.Tonase_Bongkar_KG} onChange={setInput("Tonase_Bongkar_KG")}
                  className="bg-white input-touch num-hc text-zinc-900 disabled:bg-zinc-100 disabled:cursor-not-allowed" />
                {!unlocked && <p className="text-[11px] text-zinc-400">Terbuka saat status "Bongkar" / "Selesai"</p>}
              </div>
              <div className="space-y-1.5">
                <Label className="text-zinc-500">Penyusutan (KG) — otomatis</Label>
                <div data-testid="display-penyusutan" className={`min-h-10 flex items-center px-3 rounded-md border num-hc ${penyusutanRaw > 0 ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-red-300 bg-red-50 text-red-800"}`}>{penyusutan}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {isEdit && user?.role === "owner" && <TripExpenses tripId={id} statusTrip={form.Status_Trip} komoditas={form.Jenis_Komoditas} />}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pb-8">
          <Button data-testid="cancel-trip-button" type="button" variant="outline" onClick={() => navigate("/trips")} className="border-zinc-300 btn-touch">
            Batal
          </Button>
          <Button data-testid="save-trip-button" onClick={handleSubmit} disabled={saving || !form.Plat_Truk || !form.Nama_Supir || !form.Jenis_Komoditas}
            className="bg-emerald-700 hover:bg-emerald-800 text-white transition-colors duration-200 btn-touch">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {isEdit ? "Simpan Perubahan" : "Simpan Trip"}
          </Button>
        </div>
      </div>
    </div>
  );
}
